import { Trans, useLingui } from '@lingui/react/macro';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Divider, Link, Typography } from '@mui/material';
import * as React from 'react';
import { getAddress } from 'viem';
import { BaseDialog, type DialogKey, useDialogStore } from '@/components/dialogs';
import { Button } from '@/index.tsx';
import useAuthStore from '../../../store/rootStore';
import { useAccessRequestState } from '../hooks/useAccessRequestState.ts';
import { InitialStep, RestrictedStep } from './access_steps.tsx';

const DIALOG_KEY: DialogKey = 'accessRequestDialog';

export const AccessRequestDialog = () => {
  const { t } = useLingui();
  const {
    accessCode,
    setAccessCode,
    isLoading,
    currentStep,
    setCurrentStep,
    error,
    address,
    clearError,
    resetToInitial,
    submitAccessCode,
  } = useAccessRequestState();

  const closeDialog = useDialogStore((state) => state.closeDialog);
  const isOpen = useDialogStore((state) => !!state.openDialogs[DIALOG_KEY]);
  const wasOpen = React.useRef(isOpen);
  const discordUrl = useAuthStore((s) => s.config?.features.discordUrl);

  React.useEffect(() => {
    if (!wasOpen.current && isOpen) {
      const pendingRequest = useAuthStore.getState().pendingAccessRequest;
      if (pendingRequest?.errorStatus === 403) {
        setCurrentStep('restricted');
      } else {
        resetToInitial();
      }
    }
    wasOpen.current = isOpen;
  }, [isOpen, setCurrentStep, resetToInitial]);

  const displayAddress = React.useMemo(() => {
    if (!address) return '';
    const checksumAddress = getAddress(address);
    return `${checksumAddress.slice(0, 6)}...${checksumAddress.slice(-3)}`;
  }, [address]);

  const handleDialogClose = () => {
    closeDialog(DIALOG_KEY);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (currentStep === 'accessGranted') {
      handleDialogClose();
      closeDialog('termsDialog');
      return;
    }

    await submitAccessCode();
  };

  const renderDialogActions = React.useCallback(() => {
    if (currentStep === 'restricted') {
      return null;
    }

    if (currentStep === 'accessGranted') {
      return (
        <Box sx={{ display: 'flex', width: '100%' }}>
          <Button type="submit" variant="contained" size="large" fullWidth color="primary">
            <Trans>Enter Perpl</Trans>
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%' }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isLoading || !accessCode.trim() || !!error}
          fullWidth
          color="primary"
        >
          {error ? t`Invalid Access Code` : isLoading ? t`Verifying...` : t`Confirm`}
        </Button>
        {!!error && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              <Trans>
                Need a code? Request one in our{' '}
                <Link href={discordUrl} target="_blank" rel="noopener noreferrer">
                  Discord
                </Link>
                .
              </Trans>
            </Typography>
          </>
        )}
      </Box>
    );
  }, [currentStep, isLoading, accessCode, error, discordUrl, t]);

  const renderStepContent = React.useCallback(() => {
    switch (currentStep) {
      case 'enterAccessCode':
        return (
          <InitialStep
            accessCode={accessCode}
            onAccessCodeChange={setAccessCode}
            error={error}
            isLoading={isLoading}
            onErrorClear={clearError}
            displayAddress={displayAddress}
          />
        );
      case 'accessGranted':
        return (
          <Typography>
            <Trans>Use the Bridge to deposit some AUSD and get started.</Trans>
          </Typography>
        );
      case 'restricted':
        return <RestrictedStep />;
      default:
        return null;
    }
  }, [currentStep, accessCode, setAccessCode, error, isLoading, clearError, displayAddress]);

  const title = React.useMemo(() => {
    if (currentStep === 'accessGranted') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          <Trans>Success!</Trans>
        </Box>
      );
    }
    if (currentStep === 'restricted') {
      return t`Access Restricted`;
    }
    return t`Join Perpl`;
  }, [currentStep, t]);

  return (
    <BaseDialog dialogKey={DIALOG_KEY} title={title} onSubmit={handleSubmit} actions={renderDialogActions()} maxWidth="xs">
      {renderStepContent()}
    </BaseDialog>
  );
};
